// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Country, getISO2Country } from 'utils';
import { ViewStateType } from 'components/ProductTemplates/types';
import { useGetCheckoutDotComCards } from 'views/Accounts/hooks/useGetCheckoutDotComCards';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { selectCheckoutDotComCards, userHasValidCards } from 'views/Accounts/utils';
import { Button } from 'components';
import { AuthUserQuery } from '__generated__/graphql';
import { useCheckoutDotComFlowCard } from 'views/Accounts/hooks/useCheckoutDotComFlowCard';
import { COUNTRIES } from '../../../../constants';
import Visa from '../../../../assets/images/visa.jpg';
import Mastercard from '../../../../assets/images/mastercard.jpg';
import { ACCEPTED_PAYMENT_METHODS } from '../../constants';
import { CardDetailsFormProps, FlowType } from './CardDetailsForm.types';
import { GET_AUTH_USER } from '../../graphql/getAuthUser';

/**
 * checkout.com Flow — FULL FLOW analogue of `CardDetailsFormFrames` (rendered by the `CardDetailsForm`
 * switch when `flowPaymentsEnabled` is ON). Keeps the same outward contract (`CardDetailsFormProps`).
 *
 * The Flow SDK submits the charge ITSELF against the backend payment session — there is no
 * `createCardPayment` call. Because the session is immutable and must carry the FINAL cardholder name /
 * consents (and, for a saved card, the `instrumentId`), card entry is split into two phases:
 *   • Phase 1 ("details") — name + consents (new card) or card selection (saved card). No session yet.
 *   • "Continue" → create the session with the final values → mount the SDK card / saved-card UI.
 *   • Phase 2 — the SDK-rendered fields; the form's Pay button drives `submit()`.
 * On approval the SDK either fires `onPaymentCompleted` (handled in the hook: finalize + navigate to
 * `successUrl`) or redirects for 3DS to `successUrl` / `failureUrl` (finalized by
 * `useFinalizeOnPaymentRedirect` at app root). The portal's existing `#*_success` / `#*_failure`
 * callback modals handle the landing.
 */
export default function CardDetailsFormFlow({
  amount,
  ContainerClassName,
  flow,
  portfolioId,
  btnText,
  onSuccessUrl,
  onFailureUrl,
  isUseExistingCard = false,
  transactionType = '',
}: CardDetailsFormProps) {
  const { t } = useTranslation();
  const [makeCardDefault, setMakeCardDefault] = React.useState(true);
  const [saveCard, setSaveCard] = React.useState(true);
  const [nameOnCard, setNameOnCard] = React.useState<string>('');
  const [country, setCountry] = React.useState<Country>('United Kingdom');
  const [viewState, setViewState] = React.useState<ViewStateType>(ViewStateType.DEFAULT);
  const [isProcessing, setIsProcessing] = useState(false);
  // Phase gate. `false` = collecting details (no session); `true` = session created, SDK mounted.
  const [detailsConfirmed, setDetailsConfirmed] = React.useState(false);
  const { data: cardsData, error: failedToGetCards, loading: loadingCards } = useGetCheckoutDotComCards();

  // checkout.com rejects the payment session without a customer name + email, so source them from the
  // authenticated user (mirrors cultx-web / the mobile app).
  const { data: authUserData } = useQuery<AuthUserQuery>(GET_AUTH_USER);
  const customerName = authUserData?.authUser?.fullName || undefined;
  const customerEmail = authUserData?.authUser?.emailAddress || undefined;

  const _cards = useMemo(() => {
    if (!cardsData) return [];
    return selectCheckoutDotComCards(cardsData);
  }, [cardsData]);

  const baseReturnUri = window.location.origin + window.location.pathname;
  const successUrl = `${baseReturnUri}${
    onSuccessUrl || (flow === FlowType.TOP_UP ? '#topUp_success' : '#addCard_success')
  }`;
  const failureUrl = `${baseReturnUri}${
    onFailureUrl || (flow === FlowType.TOP_UP ? '#topUp_failure' : '#addCard_failure')
  }`;

  const cardsOnFile = useMemo(() => {
    if (!_cards) return [];
    return _cards.filter((card) =>
      ACCEPTED_PAYMENT_METHODS.find((x) => x.toLocaleLowerCase() === card.scheme?.toLocaleLowerCase() || ''),
    );
  }, [_cards]);

  const useSavedCard = isUseExistingCard && cardsOnFile.length > 0;

  // The Flow `stored_card` SDK component renders its own saved-card picker (all cards, default first), so
  // we no longer pre-select a card in the form. We still hand the session the DEFAULT card's
  // `instrumentId` so the backend configures checkout.com's `stored_card`; the user can change card in
  // the SDK UI. (Mirrors cultx-web's `PaymentCardDetails` Flow path.)
  const defaultSavedCardId = useMemo(() => {
    const defaultCard = cardsOnFile.find((x) => x.defaultMitFlag) ?? cardsOnFile[0];
    return (defaultCard?.instrumentId as string | undefined) ?? undefined;
  }, [cardsOnFile]);

  const {
    containerRef,
    submit,
    isCardComplete,
    ready: flowReady,
    error: flowError,
  } = useCheckoutDotComFlowCard({
    componentType: useSavedCard ? 'stored_card' : 'card',
    amount,
    successUrl,
    failureUrl,
    customerName,
    customerEmail,
    billingCountry: getISO2Country(country),
    portfolioId: flow === FlowType.TOP_UP ? portfolioId : undefined,
    transactionType: transactionType || undefined,
    clientName: nameOnCard,
    storeCardFlag: saveCard || flow === FlowType.ADD_CARD,
    mitConsentedFlag: saveCard || flow === FlowType.ADD_CARD, // consent to store ⇒ opting into MIT (confirmed by HHW).
    defaultMitFlag: flow === FlowType.ADD_CARD ? makeCardDefault : false, // only default a brand-new card.
    instrumentId: useSavedCard ? defaultSavedCardId : undefined,
    // Phase 2: create the session + mount the SDK only after the user confirms their details.
    enabled: detailsConfirmed,
    onError: () => {
      setViewState(ViewStateType.ERROR_RESULT);
      setIsProcessing(false);
    },
  });

  React.useEffect(() => {
    if (failedToGetCards && flow === FlowType.ADD_CARD) {
      setViewState(ViewStateType.ERROR_RESULT);
    }
  }, [failedToGetCards, flow]);

  // Surface a Flow session / SDK init failure the same way the rest of the form reports errors.
  useEffect(() => {
    if (flowError) {
      setViewState(ViewStateType.ERROR_RESULT);
    }
  }, [flowError]);

  const validCardAlreadyOnFile = userHasValidCards(_cards);

  const handleContinue = () => setDetailsConfirmed(true);
  const handlePay = () => {
    setIsProcessing(true);
    submit();
  };

  const canContinue = useSavedCard
    ? !!defaultSavedCardId
    : nameOnCard.length >= 5 && country.length > 0 && !!customerName && !!customerEmail;

  const payDisabled = isProcessing || loadingCards || !flowReady || !isCardComplete;

  const primaryButtonLabel =
    btnText ||
    (flow === FlowType.TOP_UP ? t('account:slideout.topup.title') : t('account:payments.addCard.actions.save'));

  const buttonClass = (disabled: boolean) =>
    `w-full mt-6 rounded-xl px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
      disabled
        ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
        : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
    }`;

  const LockIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );

  return (
    <div
      className={
        ContainerClassName ||
        'flex flex-col flex-1 bg-gray-50 dark:bg-slate-900 pb-5 px-4 w-full relative overflow-x-hidden sm:w-[360px]'
      }
    >
      <div className="w-full h-full overflow-y-auto flex flex-col">
        <form className="h-full">
          <FeedbackTemplate
            onClose={() => setViewState(ViewStateType.DEFAULT)}
            onCTA={() => setViewState(ViewStateType.DEFAULT)}
            viewState={viewState}
          >
            <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl h-full w-full p-5 shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
              {/* Accepted Cards Banner */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl mb-5">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t('account:payments.addCard.acceptedCards')}
                </span>
                <div className="flex gap-2">
                  <img src={Visa} alt="visa" className="h-6 rounded" />
                  <img src={Mastercard} alt="mastercard" className="h-6 rounded" />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {/* The Flow `stored_card` SDK component (phase 2) renders the saved-card picker itself, so
                    the existing-card path has no phase-1 selection UI — just the "Continue" button below. */}

                {/* ── New-card details (phase 1, new-card path) ── */}
                {!useSavedCard && (
                  <>
                    {/* Name on Card */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        {t('account:payments.addCard.formFields.labels.name', 'Name on Card')}
                      </label>
                      <input
                        id="name"
                        className="checkout-com-text-field w-full"
                        type="text"
                        disabled={detailsConfirmed}
                        placeholder={t('account:payments.addCard.formFields.placeholders.name')}
                        value={nameOnCard}
                        onChange={(e) => setNameOnCard(e.target.value)}
                      />
                    </div>

                    {/* Country */}
                    <div>
                      <label
                        htmlFor="country"
                        className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5"
                      >
                        {t('account:payments.addCard.formFields.placeholders.country')}
                      </label>
                      <select
                        id="country"
                        disabled={detailsConfirmed}
                        className="w-full text-sm outline-none bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-3 focus:border-orange dark:focus:border-teal-500 transition-colors disabled:opacity-60"
                        value={country}
                        onChange={({ target: { value } }) => setCountry(value)}
                      >
                        {COUNTRIES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Save Card Checkbox */}
                    {(flow === FlowType.CHARGE_ADD_CARD || flow === FlowType.TOP_UP) && (
                      <label
                        htmlFor="saveCardInput"
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer"
                      >
                        <input
                          id="saveCardInput"
                          type="checkbox"
                          checked={saveCard}
                          disabled={detailsConfirmed}
                          onChange={() => setSaveCard(!saveCard)}
                          className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 transition-all cursor-pointer disabled:opacity-60"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {t('account:payments.addCard.actions.saveCardText')}
                        </span>
                      </label>
                    )}

                    {/* Make Default Checkbox */}
                    {flow === FlowType.ADD_CARD && (
                      <label
                        htmlFor="makedefault"
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer"
                      >
                        <input
                          id="makedefault"
                          type="checkbox"
                          checked={makeCardDefault}
                          disabled={!validCardAlreadyOnFile || detailsConfirmed}
                          onChange={() => setMakeCardDefault(!makeCardDefault)}
                          className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 disabled:opacity-50 transition-all cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {t('account:payments.addCard.formFields.makeDefault')}
                        </span>
                      </label>
                    )}
                  </>
                )}

                {/* ── Card fields rendered by the Flow SDK (phase 2) ── */}
                {detailsConfirmed && (
                  <div>
                    {!useSavedCard && (
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        {t('account:payments.addCard.formFields.labels.cardNumber', 'Card Number')}
                      </label>
                    )}
                    <div ref={containerRef} data-testid="flow-card-container" />
                    {!flowReady && !flowError && (
                      <span className="text-gray-400 text-xs mt-1 block">{t('common:loading', 'Loading…')}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Phase 1 → "Continue": create the session and reveal the card fields. */}
              {!detailsConfirmed && (
                <Button
                  isDisable={isProcessing || loadingCards || !canContinue}
                  onClick={handleContinue}
                  type="button"
                  className={buttonClass(isProcessing || loadingCards || !canContinue)}
                >
                  {LockIcon}
                  {t('account:payments.addCard.actions.continue', 'Continue')}
                </Button>
              )}

              {/* Phase 2 → "Pay/Save": submit the charge against the session via the SDK. */}
              {detailsConfirmed && (
                <Button
                  isDisable={payDisabled}
                  onClick={handlePay}
                  isProcessing={isProcessing}
                  type="button"
                  className={buttonClass(payDisabled)}
                >
                  {LockIcon}
                  {primaryButtonLabel}
                </Button>
              )}
            </div>
          </FeedbackTemplate>
        </form>
      </div>
    </div>
  );
}
